import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit2, 
  Trash2, 
  Package,
  FileText,
  Loader,
  MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import LoadingSpinner from '../components/LoadingSpinner';
import ExportModal from '../components/ExportModal';
import ImageSlider from '../components/ImageSlider';
import { apiUtils } from '../utils/api';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [stockFilter, setStockFilter] = useState('all'); // all, instock, outofstock
  const [sortOption, setSortOption] = useState('newest'); // newest, name-asc, name-desc, price-asc, price-desc
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  
  const observerTarget = useRef(null);
  const limit = 12;

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setSearchTerm(searchValue);
      setPage(1);
      setProducts([]);
      setHasMore(true);
    }, 300),
    []
  );

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts(true);
  }, [searchTerm, categoryFilter, statusFilter, stockFilter, sortOption]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, loadingMore, page]);

  const getSortParams = (sortOpt) => {
    switch (sortOpt) {
      case 'name-asc':
        return { sortBy: 'name', sortOrder: 'asc' };
      case 'name-desc':
        return { sortBy: 'name', sortOrder: 'desc' };
      case 'price-asc':
        return { sortBy: 'price', sortOrder: 'asc' };
      case 'price-desc':
        return { sortBy: 'price', sortOrder: 'desc' };
      case 'newest':
      default:
        return { sortBy: 'createdAt', sortOrder: 'desc' };
    }
  };

  const fetchProducts = async (reset = false) => {
    const currentPage = reset ? 1 : page;
    
    if (reset) {
      setLoading(true);
      setProducts([]);
      setPage(1);
    }

    const sortParams = getSortParams(sortOption);
    const params = {
      search: searchTerm,
      category: categoryFilter,
      ...sortParams,
      page: currentPage,
      limit,
      includeInactive: 'true'
    };
    
    // Add status filter if not 'all'
    if (statusFilter !== 'all') {
      params.isActive = statusFilter === 'active' ? 'true' : 'false';
    }
    
    // Add stock filter if not 'all'
    if (stockFilter !== 'all') {
      params.inStock = stockFilter === 'instock' ? 'true' : 'false';
    }
    
    const response = await apiUtils.fetchWithPagination('/products', params);
    
    if (response.success) {
      if (reset) {
        setProducts(response.data);
      } else {
        setProducts(prev => [...prev, ...response.data]);
      }
      setHasMore(response.data.length === limit && response.pagination.page < response.pagination.pages);
      setTotalProducts(response.pagination.total || 0);
    }
    
    setLoading(false);
  };

  const loadMoreProducts = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);

    const sortParams = getSortParams(sortOption);
    const params = {
      search: searchTerm,
      category: categoryFilter,
      ...sortParams,
      page: nextPage,
      limit,
      includeInactive: 'true'
    };
    
    // Add status filter if not 'all'
    if (statusFilter !== 'all') {
      params.isActive = statusFilter === 'active' ? 'true' : 'false';
    }
    
    // Add stock filter if not 'all'
    if (stockFilter !== 'all') {
      params.inStock = stockFilter === 'instock' ? 'true' : 'false';
    }
    
    const response = await apiUtils.fetchWithPagination('/products', params);
    
    if (response.success) {
      setProducts(prev => [...prev, ...response.data]);
      setHasMore(response.data.length === limit && response.pagination.page < response.pagination.pages);
    }
    
    setLoadingMore(false);
  };

  const fetchCategories = async () => {
    const response = await apiUtils.fetchWithPagination('/categories', { limit: 100 });
    if (response.success) {
      setCategories(response.data);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const handleCategoryChange = (value) => {
    setCategoryFilter(value);
    setPage(1);
    setProducts([]);
    setHasMore(true);
  };

  const handleSortChange = (value) => {
    setSortOption(value);
    setPage(1);
    setProducts([]);
    setHasMore(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStatusFilter('all');
    setStockFilter('all');
    setSortOption('newest');
    setPage(1);
    setProducts([]);
    setHasMore(true);
  };

  const handleExport = async (format, exportOptions) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add category filter
      if (exportOptions.category) {
        queryParams.append('category', exportOptions.category);
      }
      
      // Add status filter
      if (exportOptions.status !== 'all') {
        queryParams.append('isActive', exportOptions.status === 'active' ? 'true' : 'false');
      }
      
      // Add stock filter
      if (exportOptions.stock !== 'all') {
        queryParams.append('inStock', exportOptions.stock === 'instock' ? 'true' : 'false');
      }

      const exportUrl = `/exports/products/${format}?${queryParams}`;

      const result = await apiUtils.downloadFile(
        exportUrl,
        `products-export.${format}`
      );

      if (result.success) {
        toast.success(`Products exported as ${format.toUpperCase()} successfully`);
        setShowExportModal(false);
      } else {
        // The downloadFile function now provides detailed error messages
        toast.error(result.error);
      }
    } catch (error) {
      // This should rarely happen now since downloadFile handles most errors internally
      console.error('Unexpected export error:', error);
      toast.error('An unexpected error occurred during export');
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Delete Product?',
      text: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    const deleteResult = await apiUtils.delete(`/products/${id}`);
    
    if (deleteResult.success) {
      toast.success('Product deleted successfully');
      fetchProducts(true);
    } else {
      toast.error(deleteResult.error || 'Failed to delete product');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 break-words">Products ({totalProducts})</h1>
          <p className="mt-1 text-sm text-gray-600 break-words">
            Manage your fruit products inventory
          </p>
        </div>
        
        <div className="flex gap-2 flex-shrink-0">
          {/* Export Button */}
          <button
            onClick={() => setShowExportModal(true)}
            className="btn-secondary inline-flex items-center justify-center"
            title="Export Products"
          >
            <Download className="h-5 w-5" />
          </button>
          
          {/* Add Product Button */}
          <Link 
            to="/products/new" 
            className="btn-primary inline-flex items-center justify-center"
            title="Add Product"
          >
            <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline sm:ml-2">Add Product</span>
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          {/* Search Bar */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  defaultValue={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Start typing to search products instantly
              </p>
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary inline-flex items-center lg:self-start lg:mt-0"
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                <div>
                  <label className="form-label">Category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="form-select"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Active Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                      setProducts([]);
                      setHasMore(true);
                    }}
                    className="form-select"
                  >
                    <option value="all">All Products</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Stock Status</label>
                  <select
                    value={stockFilter}
                    onChange={(e) => {
                      setStockFilter(e.target.value);
                      setPage(1);
                      setProducts([]);
                      setHasMore(true);
                    }}
                    className="form-select"
                  >
                    <option value="all">All Products</option>
                    <option value="instock">In Stock Only</option>
                    <option value="outofstock">Out of Stock Only</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Sort By</label>
                  <select
                    value={sortOption}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="form-select"
                  >
                    <option value="newest">Newest First</option>
                    <option value="name-asc">Name (A-Z)</option>
                    <option value="name-desc">Name (Z-A)</option>
                    <option value="price-asc">Price (Low to High)</option>
                    <option value="price-desc">Price (High to Low)</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="btn-ghost w-full"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" text="Loading products..." />
        </div>
      ) : products.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product._id} className="card hover:shadow-lg transition-shadow duration-200 flex flex-col">
                {/* Product Image Slider */}
                <div className="relative h-48 bg-gray-200 rounded-t-xl overflow-hidden">
                  {product.imageUrls && product.imageUrls.length > 0 ? (
                    <ImageSlider images={product.imageUrls} productName={product.name} />
                  ) : product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Status Badges */}
                  <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
                    <span className={`badge ${product.isActive ? 'badge-success' : 'badge-danger'}`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {!product.inStock && (
                      <span className="badge badge-warning">
                        Out of Stock
                      </span>
                    )}
                  </div>
                </div>

                {/* Product Info */}
                <div className="card-body flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {product.category?.name || 'No Category'}
                      </p>
                      {/* Origin/Imported Country */}
                      {product.importedCountry && (
                        <div className="flex items-center mt-1">
                          <MapPin className="h-3 w-3 text-emerald-600 mr-1" />
                          <span className="text-xs text-emerald-700 font-medium">
                            Origin: {product.importedCountry}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="inline-flex items-baseline gap-1">
                      {typeof product.price === 'number' && product.price > 0 ? (
                        <>
                          <span className="text-2xl font-bold text-brand-600">
                            {product.price.toFixed(2)} AED
                          </span>
                          <span className="text-sm font-medium text-gray-500">
                            /{product.unit?.toUpperCase() || 'UNIT'}
                          </span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-gray-500">
                          Not Available
                        </span>
                      )}
                    </div>
                  </div>

                  {product.remark && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {product.remark}
                    </p>
                  )}

                  {/* Spacer - grows to push SKU section to bottom */}
                  <div className="flex-grow"></div>

                  {/* Actions - always at bottom */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <span className="text-xs text-gray-500">
                      SKU: {product.sku}
                    </span>
                    
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/products/edit/${product._id}`}
                        className="p-2 text-gray-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                        title="Edit product"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Link>
                      
                      <button
                        onClick={() => handleDelete(product._id, product.name)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete product"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <div ref={observerTarget} className="flex justify-center py-8">
              {loadingMore && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Loading more products...</span>
                </div>
              )}
            </div>
          )}

          {!hasMore && products.length > limit && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">
                You've reached the end of the list
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || categoryFilter || statusFilter !== 'all' || stockFilter !== 'all'
              ? 'No products match your search criteria. Try adjusting your filters.'
              : 'Get started by creating a new product.'}
          </p>
          <div className="mt-6">
            <Link to="/products/new" className="btn-primary inline-flex items-center">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Link>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        categories={categories}
        onExport={handleExport}
      />
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default Products;